import { type PlayVideoProperties } from '@src/ampli-web-digital'
import { getLangs } from '@src/i18n/locale.util'
import { CocoaEntityType } from '@src/utils/constants.util'
import { omitNullish } from '@src/utils/object.util'

export type AnalyticsContentType =
  'program' | 'block' | 'compilation' | 'workout' | 'recipe' | 'episode' | 'lesson' | 'promo'

/** Maps a CoCoA entity type to the ampli content type enum (e.g. Block -> block). */
export function getAnalyticsContentType(
  type?: CocoApi.EntityType | CocoApi.ContentType
): AnalyticsContentType | undefined {
  switch (type) {
    case CocoaEntityType.program:
      return 'program'
    case CocoaEntityType.bodiBlock:
      return 'block'
    case CocoaEntityType.compilation:
      return 'compilation'
    case CocoaEntityType.workout:
      return 'workout'
    case CocoaEntityType.lesson:
      return 'lesson'
    case CocoaEntityType.recipe:
      return 'recipe'
    case CocoaEntityType.episode:
      return 'episode'
    case CocoaEntityType.promo:
      return 'promo'
    default:
      return undefined
  }
}

/**
 * Returns object of common page URL properties used across all Amplitude page view events.
 */
export function getPageURLProperties(href: string) {
  const url = new URL(href)
  const slug = url.pathname.split('/').slice(-1)[0]
  const searchParams = url.searchParams
  const langs = getLangs()
  return {
    'page path': url.pathname.toLowerCase(),
    'page slug': ['', ...langs].includes(slug) ? '(home)' : slug.toLowerCase(),
    'page url': url.href.toLowerCase(),
    'query params': searchParams.toString() || 'N/A',
    'web source code': searchParams.get('code')?.toLowerCase() ?? undefined,
  }
}

export function getPageGlobalProps(href: string) {
  const pageUrlProps = getPageURLProperties(href)
  return {
    'page slug': pageUrlProps['page slug'],
    'page path': pageUrlProps['page path'],
    'page url': pageUrlProps['page url'],
  }
}

export type PlayVideoAnalytics = PlayVideoProperties

type CocoaEntity = CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity

/** Maps each scenario name to its required params type. */
type PlayerAnalyticsScenarioMap = {
  jumpBackIn: {
    entity: CocoaEntity
    videoIndex: number
    suggestedVideoIndex: number
    collectionSlug: string
    isEnrolled: boolean
  }
  collection: {
    entity: CocoaEntity
    videoIndex: number
    collectionSlug: string
    isEnrolled?: boolean
  }
  contentDetail: {
    entity: CocoaEntity
    isEnrolled?: boolean
  }
  fitnessHub: {
    entity: CocoaEntity
    videoIndex: number
    tabName: 'programs' | 'workouts' | 'daily activity' | 'my progress'
    collectionSlug?: string
  }
  search: {
    entity: CocoaEntity
    videoIndex: number
  }
  badgeDetail: {
    item: GamificationApi.CuratedItem
    programSlug?: string
  }
  quizResults: {
    entity: CocoaEntity
    programSlug: string
  }
}

function toPlayVideoCatalog(catalog?: string): 'bod' | 'bodi' | undefined {
  if (catalog === 'bod' || catalog === 'bodi') return catalog
  return undefined
}

/**
 * Assembles the complete analytics payload for a given play scenario.
 * Reads both entity-derived fields (slug, title, catalog, parent, enrollment)
 * and the scenario-specific contextual fields (index, collection, tab, etc.).
 */
export function getPlayerAnalytics<S extends keyof PlayerAnalyticsScenarioMap>(
  scenario: S,
  params: PlayerAnalyticsScenarioMap[S]
): PlayVideoAnalytics {
  switch (scenario) {
    case 'jumpBackIn': {
      const { entity, videoIndex, suggestedVideoIndex, collectionSlug, isEnrolled } =
        params as PlayerAnalyticsScenarioMap['jumpBackIn']
      return omitNullish({
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': entity.primaryParent?.slug,
        'is enrolled': isEnrolled,
        'video index': videoIndex,
        'suggested video index': suggestedVideoIndex,
        'collection slug': collectionSlug,
      }) as PlayVideoAnalytics
    }

    case 'collection': {
      const { entity, videoIndex, collectionSlug, isEnrolled } =
        params as PlayerAnalyticsScenarioMap['collection']
      return omitNullish({
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': entity.primaryParent?.slug,
        'is enrolled': isEnrolled,
        'video index': videoIndex,
        'collection slug': collectionSlug,
      }) as PlayVideoAnalytics
    }

    case 'contentDetail': {
      const { entity, isEnrolled } = params as PlayerAnalyticsScenarioMap['contentDetail']
      return omitNullish({
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': entity.primaryParent?.slug,
        'is enrolled': isEnrolled,
      }) as PlayVideoAnalytics
    }

    case 'fitnessHub': {
      const { entity, videoIndex, tabName, collectionSlug } =
        params as PlayerAnalyticsScenarioMap['fitnessHub']
      return omitNullish({
        'screen name': 'fitness',
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': entity.primaryParent?.slug,
        'is enrolled': entity.userData?.isEnrolled,
        'collection slug': collectionSlug,
        'video index': videoIndex,
        'tab name': tabName,
      }) as PlayVideoAnalytics
    }

    case 'search': {
      const { entity, videoIndex } = params as PlayerAnalyticsScenarioMap['search']
      return omitNullish({
        'screen name': 'search',
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': entity.primaryParent?.slug,
        'is enrolled': entity.userData?.isEnrolled,
        'video index': videoIndex,
      }) as PlayVideoAnalytics
    }

    case 'badgeDetail': {
      const { item, programSlug } = params as PlayerAnalyticsScenarioMap['badgeDetail']
      return omitNullish({
        'video slug': item.slug,
        'screen name': 'badge detail',
        'video title': item.title,
        catalog: toPlayVideoCatalog(item.catalog),
        'video parent slug': programSlug?.toLowerCase(),
      }) as PlayVideoAnalytics
    }

    case 'quizResults': {
      const { entity, programSlug } = params as PlayerAnalyticsScenarioMap['quizResults']
      return omitNullish({
        'screen name': 'quiz results',
        'video slug': entity.slug,
        'video title': entity.title,
        catalog: entity.catalog as 'bod' | 'bodi' | undefined,
        'video parent slug': programSlug,
      }) as PlayVideoAnalytics
    }
  }
}

// ---------------------------------------------------------------------------
// Route-derived screen / tab name helpers (used by useVideoPlayer internally)
// ---------------------------------------------------------------------------

/**
 * Derives the Amplitude `screen name` property from the current Next.js pathname.
 * Call sites that know their exact screen name can override this by passing `screen name`
 * explicitly in the `analytics` payload to `playVideo`.
 */
export function getVideoPlayerScreenName(pathname: string): string {
  if (/\/(programs|content|blocks)\//.test(pathname)) return 'content detail'
  if (/\/workouts/.test(pathname)) return 'fitness'
  if (/\/search/.test(pathname)) return 'search'
  if (/\/favorites/.test(pathname)) return 'all favorites'
  if (/\/badges/.test(pathname)) return 'badge detail'
  return 'home'
}

/**
 * Derives the Amplitude `tab name` property from the current pathname, when applicable.
 */
export function getVideoPlayerTabName(
  pathname: string
): 'programs' | 'workouts' | 'daily activity' | 'my progress' | undefined {
  if (/\/workouts/.test(pathname)) return 'workouts'
  return undefined
}
